import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  fetchSurveyAccessToken,
  fetchSurveyStatus,
} from "../helpers/apiHelper";
import { clearSurveyActive, markSurveyActive } from "../helpers/surveyActivity";
import "../styles/SurveyViewer.css";
import FullscreenLoader from "./FullscreenLoader";

const SURVEY_MESSAGE_VERSION = 1;

const getSurveyStatusStorageKey = (matterId) =>
  `bhp_dispute_survey_status_${matterId || "unknown"}`;

const writeStoredSurveyStatus = (matterId, value) => {
  try {
    if (!matterId || !value) return;
    sessionStorage.setItem(
      getSurveyStatusStorageKey(matterId),
      JSON.stringify(value),
    );
  } catch {}
};

export default function SurveyViewer() {
  const navigate = useNavigate();
  const location = useLocation();
  const iframeRef = useRef(null);

  const [loaded, setLoaded] = useState(false);
  const [showClose, setShowClose] = useState(false);
  const [closingSurvey, setClosingSurvey] = useState(false);

  const [surveyToken, setSurveyToken] = useState(null);
  const [loadingToken, setLoadingToken] = useState(true);
  const [tokenError, setTokenError] = useState("");

  const invitationId = location.state?.invitationId || "";
  const matterId = location.state?.matterId || "";
  const partyId = location.state?.partyId || "";

  const baseSurveyUrl =
    location.state?.url ||
    `https://pgsurvey-dev.pogustgoodhead.com/invite/${invitationId}`;

  const token =
    location.state?.token || localStorage.getItem("accessToken") || "";

  const surveyUrl = useMemo(() => {
    if (!baseSurveyUrl) return "";
    const separator = baseSurveyUrl.includes("?") ? "&" : "?";
    return `${baseSurveyUrl}${separator}embedded=portal&lang=pt`;
  }, [baseSurveyUrl]);

  const surveyOrigin = useMemo(() => {
    try {
      return new URL(baseSurveyUrl).origin;
    } catch {
      return "";
    }
  }, [baseSurveyUrl]);

  useEffect(() => {
    const timer = setTimeout(() => setShowClose(true), 1000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    markSurveyActive();
    return () => {
      clearSurveyActive();
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadToken = async () => {
      if (!token || !invitationId || !matterId) {
        setTokenError("Missing required data for survey.");
        setLoadingToken(false);
        return;
      }

      try {
        const response = await fetchSurveyAccessToken(
          token,
          invitationId,
          matterId,
        );

        if (cancelled) return;

        if (!response?.accessToken) {
          throw new Error("Missing survey access token");
        }

        setSurveyToken(response);
      } catch (err) {
        if (cancelled) return;
        setTokenError(err.message || "Failed to load survey token");
      } finally {
        if (!cancelled) setLoadingToken(false);
      }
    };

    loadToken();

    return () => {
      cancelled = true;
    };
  }, [token, invitationId, matterId]);

  useEffect(() => {
    if (!surveyOrigin || !surveyToken) return;

    const postToSurvey = (payload) => {
      const iframeWindow = iframeRef.current?.contentWindow;
      if (!iframeWindow) return;
      iframeWindow.postMessage(payload, surveyOrigin);
    };

    const handleMessage = (event) => {
      if (event.origin !== surveyOrigin) return;

      const data = event.data;
      if (!data || typeof data !== "object") return;

      console.log("[portal] <- survey message", data);

      const requestedInvitationId = data?.invitationId || invitationId;
      const correlationId = data?.correlationId;

      if (!requestedInvitationId || !correlationId) return;

      if (data.type === "pgs-survey.request-survey-access-token") {
        postToSurvey({
          type: "pgs-survey.survey-access-token",
          version: SURVEY_MESSAGE_VERSION,
          correlationId,
          invitationId: requestedInvitationId,
          accessToken: surveyToken.accessToken,
          expiresAt: surveyToken.expiresAt || null,
        });
      }

      if (data.type === "pgs-survey.refresh-survey-access-token") {
        postToSurvey({
          type: "pgs-survey.survey-access-token",
          version: SURVEY_MESSAGE_VERSION,
          correlationId,
          invitationId: requestedInvitationId,
          accessToken: surveyToken.accessToken,
          expiresAt: surveyToken.expiresAt || null,
        });
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [surveyOrigin, surveyToken, invitationId]);

  const handleIframeLoad = () => {
    setLoaded(true);

    const iframeWindow = iframeRef.current?.contentWindow;
    if (!iframeWindow || !surveyOrigin) return;

    setTimeout(() => {
      iframeWindow.postMessage(
        {
          type: "pgs-survey.host-ready",
          version: SURVEY_MESSAGE_VERSION,
        },
        surveyOrigin,
      );
    }, 300);
  };

  const handleClose = async () => {
    if (closingSurvey) return;

    try {
      setClosingSurvey(true);

      let surveyStatus = null;

      if (token && invitationId) {
        surveyStatus = await fetchSurveyStatus(token, invitationId);
      }

      if (surveyStatus) {
        writeStoredSurveyStatus(matterId, surveyStatus);
      }

      navigate("/dispute", {
        replace: true,
        state: {
          surveyStatus,
          invitationId,
          partyId,
          matterId,
          refreshedAt: Date.now(),
        },
      });
    } catch {
      navigate("/dispute", {
        replace: true,
        state: {
          surveyStatus: null,
          invitationId,
          partyId,
          matterId,
          refreshedAt: Date.now(),
        },
      });
    } finally {
      setClosingSurvey(false);
    }
  };

  return (
    <div className="surveyFullscreen">
      <button
        type="button"
        className={`surveyCloseTab ${showClose ? "visible" : ""}`}
        onClick={handleClose}
        disabled={closingSurvey}
      >
        Fechar
      </button>

      {loadingToken || closingSurvey ? (
        <FullscreenLoader
          title="Aguarde enquanto preparamos seu formulário"
          message={
            closingSurvey
              ? "Estamos atualizando o status da sua contestação."
              : "Este processo pode levar alguns minutos."
          }
        />
      ) : tokenError ? (
        <div className="surveyEmptyState">Este formulário já foi enviado.</div>
      ) : surveyUrl ? (
        <iframe
          ref={iframeRef}
          src={surveyUrl}
          title="Questionário"
          className={`surveyFullscreenFrame ${loaded ? "loaded" : ""}`}
          onLoad={handleIframeLoad}
        />
      ) : (
        <div className="surveyEmptyState">Questionário não encontrado.</div>
      )}
    </div>
  );
}
